import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, MessageSquareText } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupMessages, type AdminMessage } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.13 — Xabar tarixi (moderatsiya konteksti). GET /admin/groups/:id/messages
 * (backend `e61ca2c`). O'chirilgan xabarlar belgi bilan ko'rsatiladi, yashirilmaydi.
 */
export function GroupMessages() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminMessage>[]>(
    () => [
      { key: 'seq', header: '#', type: 'text', width: 70 },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'sender_id',
        header: t.col.name,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.sender_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.sender_id}`}
          </Link>
        ),
      },
      { key: 'type', header: t.col.type, type: 'text', width: 110 },
      {
        key: 'text',
        header: t.groups.messageText,
        type: 'longtext',
        minWidth: 240,
        sortable: false,
        render: (r) => (
          <span className={r.deleted ? 'text-tertiary line-through' : ''}>{r.text || '—'}</span>
        ),
      },
      { key: 'view_count', header: t.groups.views, type: 'number', width: 90, align: 'right' },
      { key: 'edited', header: t.groups.edited, type: 'bool', width: 90, hiddenByDefault: true },
      { key: 'deleted', header: t.groups.deleted, type: 'bool', width: 100 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupMessages(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.messages}
        screenId="A4.13"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminMessage>
        screenId="A4.13"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.seq)}
        exportable
        emptyState={{ icon: <MessageSquareText size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
